/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import { Injectable, inject } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { Store } from '@ngxs/store'
import { Observable } from 'rxjs'
import { AddressWidgetState } from '../store/address-widget.state'
import { SelectAddress, SetAddressBeingEdited } from '../store/list.actions'
import { AddressDto } from '../../../../../apps/myprofile-ui/src/app/services/address/address.service'

@Injectable({
  providedIn: 'root',
})
export class MyProfileAddressListService {
  private store = inject(Store)
  private _translateService = inject(TranslateService)

  public hasItems$: Observable<boolean> = this.store.select(AddressWidgetState.hasAddresses)
  public items$: Observable<AddressDto[]> = this.store.select(({ addressWidget }) => addressWidget.addresses)
  public selectedItemId$: Observable<string> = this.store.select(({ addressWidget }) => addressWidget.addressList.selectedAddressId)
  public radioListDisabled$: Observable<boolean> = this.store.select(({ addressWidget }) => addressWidget.addressList.disabled)

  public itemIdBeingEdited$!: Observable<string>

  public selectItem(itemId: string, instanceId: string): any {
    this.store.dispatch(new SelectAddress(itemId, instanceId))
  }

  public editItem(item: AddressDto, instanceId: string): any {
    this.store.dispatch(new SetAddressBeingEdited(item.addressId as string, instanceId))
  }

  public selectInitialItem(instanceId: string): any {
    const addresses = this.store.selectSnapshot((state) => state.addressWidget.addresses)
    const addressId = addresses.length ? (addresses.find((address: AddressDto) => address.isStandard)?.addressId ?? null) : null
    this.store.dispatch(new SelectAddress(addressId, instanceId))
  }

  public editButtonAriaLabel(item: AddressDto): string {
    const prefix = this._translateService.instant('pidpaw.selectAddress.button.editAddress.aria')
    return `${prefix} ${this.composeAddressForA11y(item)}`
  }

  public deleteButtonAriaLabel(item: AddressDto): string {
    const prefix = this._translateService.instant('pidpaw.editAddress.button.delete.aria')
    return `${prefix} ${this.composeAddressForA11y(item)}`
  }

  private composeAddressForA11y(item: AddressDto): string {
    const parts: string[] = []
    if (item.isStandard) {
      parts.push(this._translateService.instant('pidpaw.selectAddress.address.preferredMarker'))
    }
    if (item.usage) {
      parts.push(this.translatedAddressType[item.usage])
    }
    parts.push((item.formattedAddress ?? '').replace(/\n/g, ', '))
    return parts.join(', ')
  }

  private get translatedAddressType(): { home: string; work: string } {
    return {
      home: this._translateService.instant('pidpaw.editAddress.radio.addressTypeHome.label'),
      work: this._translateService.instant('pidpaw.editAddress.radio.addressTypeWork.label'),
    }
  }
}
